import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import type { CfoBindingSetupStatus, CfoMaterializationStatusResponse, ServerApiClient } from '../../../data/ServerApiClient'
import { cfoRefusalSentence } from './cfoRefusalCopy'
import { trimStr } from './cfoFormat'
import { invalidateCfoBootstrapCache } from './useCfoBootstrap'
import './CfoMaterializationBanner.css'

const POLL_MS = 3000
const IDLE_POLL_MS = 30_000
// A dropped status call used to strand the banner forever: the poll below only
// runs while a materialization is active, so after a failed `ensure` (status
// stays null) nothing ever retried and only a page reload cleared it. Retry on
// our own backoff instead, so a network blip heals itself.
const ERROR_RETRY_BASE_MS = 4000
const ERROR_RETRY_MAX_MS = 30_000
/** Past this many consecutive failures, stop implying it will fix itself. */
const ERROR_PERSISTENT_AFTER = 3

const statusPromises = new Map<string, Promise<CfoMaterializationStatusResponse | null>>()

// Never show a client a raw fetch rejection. `Failed to fetch` (Chrome) /
// `Load failed` (Safari) / `NetworkError…` (Firefox) all mean the same thing to
// the person reading it: we could not reach the server. A typed refusal becomes
// the plain sentence `cfoRefusalCopy` owns; anything else still shows its server
// text, minus the machine code prefix that also travels in the JSON `error`.
/** What a person reads. Never carries the HTTP status or a refusal code. */
export function materializationErrorSentence(raw: unknown): string {
  const message = trimStr(raw)
  if (!message) return 'We could not reach FuseGrid to check for new data.'
  if (/failed to fetch|load failed|networkerror|network request failed|fetch failed/i.test(message)) {
    return 'We could not reach FuseGrid to check for new data.'
  }
  if (/aborted|timeout|timed out/i.test(message)) {
    return 'The check for new data timed out before it finished.'
  }
  return cfoRefusalSentence(message)
}

// The banner classifies the failure from this string (`materializationHttpStatus`)
// and renders its own copy for every HTTP answer, so the status stays on the
// stored error and never reaches a reader. Surfaces that render the refusal
// itself use `materializationErrorSentence`.
export function humanizeMaterializationError(raw: unknown): string {
  const sentence = materializationErrorSentence(raw)
  const status = materializationHttpStatus(trimStr(raw))
  return status === null ? sentence : `API ${status}: ${sentence}`
}

export function materializationHttpStatus(error: string): number | null {
  const match = error.match(/\bAPI (\d{3})\b/)
  return match ? Number(match[1]) : null
}

export function isRetryableMaterializationError(error: string): boolean {
  const status = materializationHttpStatus(error)
  return status === null || status === 408 || status === 428 || status === 429 || status >= 500
}

export function resetCfoMaterializationSessionForTests() {
  statusPromises.clear()
}

function materializationActive(status: CfoMaterializationStatusResponse | null): boolean {
  const runStatus = String(status?.refresh?.status || '')
  return status?.status === 'refreshing'
    || (status?.status === 'first_run' && ['queued', 'running', 'retry'].includes(runStatus))
}

function materializationDone(status: CfoMaterializationStatusResponse | null): boolean {
  return Boolean(status && ['ready', 'failed', 'blocked', 'no_binding'].includes(String(status.status)))
}

// The server's stale-reason codes are engineering vocabulary. They were being
// joined and printed straight into a client-facing banner ("no_binding"), which
// tells the reader nothing they can act on. Unknown codes still pass through —
// an unrecognized code is better shown than swallowed.
const STALE_REASON_COPY: Record<string, string> = {
  no_binding: 'CFO reports are not set up for this workbook yet.',
  missing_source_data: 'We don’t have enough synced QuickBooks data yet.',
  missing_core_artifacts: 'The core financial reports still need to be built.',
  core_artifacts_stale: 'The core financial reports need rebuilding against your latest books.',
  ar_artifact_stale: 'The A/R analysis needs rebuilding against your latest books.',
  valuation_artifacts_stale: 'The valuation needs rebuilding against your latest books.',
  cash_planning_stale: 'The 13-week cash plan needs rebuilding against your latest books.',
  financial_analysis_stale: 'The financial analysis needs rebuilding against your latest books.',
  forecast_package_missing: 'The forecast hasn’t been built yet.',
  missing_forecast_package: 'The forecast hasn’t been built yet.',
  forecast_config_stale: 'The forecast needs rebuilding against your latest books.',
  forecast_model_version_stale: 'The forecast needs rebuilding against your latest books.',
  forecast_window_stale: 'The forecast needs rebuilding against your latest books.',
  forecast_source_fingerprint_stale: 'The forecast needs rebuilding against your latest books.',
  forecast_source_fingerprint_missing: 'The forecast needs rebuilding against your latest books.',
  stale_package_id: 'The forecast needs rebuilding against your latest books.',
}

export function humanizeStaleReasons(reasons: string[] | null | undefined): string {
  const sentences: string[] = []
  for (const reason of reasons || []) {
    const key = trimStr(reason).toLowerCase()
    if (!key) continue
    const copy = STALE_REASON_COPY[key] || `Needs attention: ${key.replace(/_/g, ' ')}.`
    if (!sentences.includes(copy)) sentences.push(copy)
  }
  return sentences.join(' ')
}

export function useCfoMaterialization(
  appApi: ServerApiClient,
  workbookId: string | null,
  apiSessionReady = true,
  requestedPeriod?: string | null,
) {
  const [status, setStatus] = useState<CfoMaterializationStatusResponse | null>(null)
  const [error, setError] = useState('')
  /** Consecutive failed status checks. Drives the retry backoff and the copy. */
  const [errorAttempts, setErrorAttempts] = useState(0)
  const [retryNonce, setRetryNonce] = useState(0)
  const wasRefreshing = useRef(false)

  const retryNow = useCallback(() => setRetryNonce((nonce) => nonce + 1), [])

  const fetchStatus = useCallback(async () => {
    const wb = trimStr(workbookId)
    if (!wb || !apiSessionReady) return null
    try {
      const next = await appApi.getCfoMaterializationStatus(wb, requestedPeriod || undefined)
      setStatus(next)
      setError('')
      setErrorAttempts(0)
      return next
    } catch (err: any) {
      setError(humanizeMaterializationError(err?.message || err))
      setErrorAttempts((attempts) => attempts + 1)
      return null
    }
  }, [apiSessionReady, appApi, requestedPeriod, workbookId])

  useEffect(() => {
    const wb = trimStr(workbookId)
    if (!wb || !apiSessionReady) {
      setStatus(null)
      setError('')
      setErrorAttempts(0)
      return
    }
    let cancelled = false
    // Navigation only observes durable work. A retry of this read must never
    // enqueue paid processing, even when the first status request was lost.
    const statusKey = `${wb}:${requestedPeriod || 'latest'}`
    const pending = statusPromises.get(statusKey) || appApi.getCfoMaterializationStatus(wb, requestedPeriod || undefined)
      .finally(() => statusPromises.delete(statusKey))
    statusPromises.set(statusKey, pending)
    void pending.then((next) => {
      if (cancelled) return
      setStatus(next)
      setError('')
      setErrorAttempts(0)
      if (materializationActive(next)) wasRefreshing.current = true
    }).catch((err: unknown) => {
      if (cancelled) return
      setError(humanizeMaterializationError(err instanceof Error ? err.message : err))
      setErrorAttempts((attempts) => attempts + 1)
    })
    return () => {
      cancelled = true
    }
  }, [apiSessionReady, appApi, fetchStatus, requestedPeriod, retryNonce, workbookId])

  // Retry only the read after a transient status failure.
  useEffect(() => {
    if (!error || !isRetryableMaterializationError(error)) return
    const wb = trimStr(workbookId)
    if (!wb || !apiSessionReady) return
    const delay = Math.min(ERROR_RETRY_MAX_MS, ERROR_RETRY_BASE_MS * 2 ** Math.max(0, errorAttempts - 1))
    const timer = window.setTimeout(retryNow, delay)
    return () => window.clearTimeout(timer)
  }, [apiSessionReady, error, errorAttempts, retryNow, workbookId])

  useEffect(() => {
    const wb = trimStr(workbookId)
    if (!wb || !apiSessionReady || (error && !isRetryableMaterializationError(error))) return
    if (materializationActive(status)) wasRefreshing.current = true
    let cancelled = false
    const timer = window.setInterval(() => {
      void fetchStatus().then((next) => {
        if (cancelled || !next) return
        if (wasRefreshing.current && materializationDone(next)) {
          wasRefreshing.current = false
          invalidateCfoBootstrapCache(wb)
          window.dispatchEvent(new CustomEvent('fg:cfo-materialization-complete', { detail: { workbookId: wb } }))
        }
      })
    }, materializationActive(status) ? POLL_MS : IDLE_POLL_MS)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [apiSessionReady, error, fetchStatus, status, workbookId])

  // First-time statement artifacts are built by the bootstrap path. Refresh
  // its previously blocked status when that build completes as well.
  useEffect(() => {
    const onComplete = (event: Event) => {
      const detail = (event as CustomEvent<{ workbookId?: string; source?: string }>).detail
      if (detail?.workbookId === workbookId && detail.source === 'bootstrap') {
        void fetchStatus()
      }
    }
    window.addEventListener('fg:cfo-materialization-complete', onComplete)
    return () => window.removeEventListener('fg:cfo-materialization-complete', onComplete)
  }, [fetchStatus, workbookId])

  return { status, error, errorAttempts, retryNow, refreshStatus: fetchStatus }
}

function formatFrozenAsOf(iso: string | null | undefined): string | null {
  if (!iso) return null
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
}

// The reconnect CTA must NOT be a plain `<a href={reconnect_url}>`: that URL is
// the JWT-guarded `/connect` endpoint, and a full-page browser navigation to it
// carries no `Authorization` header, so the client lands on a raw
// `missing_token` error (Byran Geels, 2026-07-06). Instead we mint an authorize
// URL through the same POST `/authorize` path every other reconnect surface
// uses (CfoCoreFileMenu, CfoExperiencesPage) and then hand the browser off to
// Intuit's consent screen.
function CfoMaterializationReconnectButton({
  appApi,
  workbookId,
  realmId,
}: {
  appApi: ServerApiClient
  workbookId: string
  realmId: string | null
}) {
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const startReconnect = useCallback(async () => {
    setBusy(true)
    setFailed(false)
    try {
      const out = await appApi.createQuickBooksAuthorizeUrl({
        realm_id: realmId,
        workbook_id: workbookId,
        redirect: window.location.href,
      })
      if (!out?.authorize_url) throw new Error('missing authorize_url')
      window.location.href = out.authorize_url
    } catch {
      setFailed(true)
      setBusy(false)
    }
  }, [appApi, realmId, workbookId])
  return (
    <button
      type="button"
      className="cfo-materialization-reconnect"
      onClick={() => void startReconnect()}
      disabled={busy}
    >
      {busy
        ? 'Starting QuickBooks…'
        : failed
          ? 'Couldn’t start — try again'
          : 'Reconnect QuickBooks →'}
    </button>
  )
}

/**
 * Explicit setup for an unbound workbook whose accounts are synced: bind the
 * statement layout, then request the first report update through the same
 * durable admission as "Update reports". Nothing here runs on render.
 */
function CfoBindingSetupButton({ appApi, workbookId, requestedPeriod, onSetUp }: {
  appApi: ServerApiClient; workbookId: string; requestedPeriod?: string | null; onSetUp?: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const setUp = async () => {
    setBusy(true)
    setError('')
    let bound = false
    try {
      await appApi.setUpCfoReports(workbookId)
      bound = true
      invalidateCfoBootstrapCache(workbookId)
      window.dispatchEvent(new CustomEvent('fg:cfo-materialization-complete', { detail: { workbookId, source: 'binding_setup' } }))
      await appApi.ensureCfoMaterialization(workbookId, requestedPeriod || undefined)
      onSetUp?.()
    } catch (err) {
      const message = materializationErrorSentence(err instanceof Error ? err.message : err)
      setError(bound ? `CFO reports are set up, but the first report update did not start. ${message}` : message)
      if (bound) onSetUp?.()
    } finally {
      setBusy(false)
    }
  }
  return <div>
    <button type="button" className="cfo-materialization-retry" disabled={busy} onClick={() => void setUp()}>
      {busy ? 'Setting up reports…' : 'Set up CFO reports'}
    </button>
    {error ? <span role="alert">{error}</span> : null}
  </div>
}

function CfoReportRefreshButton({ appApi, workbookId, requestedPeriod, onQueued }: {
  appApi: ServerApiClient; workbookId: string; requestedPeriod?: string | null; onQueued?: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const refresh = async () => {
    setBusy(true)
    setError('')
    try {
      await appApi.ensureCfoMaterialization(workbookId, requestedPeriod || undefined)
      onQueued?.()
    } catch (err) {
      setError(materializationErrorSentence(err instanceof Error ? err.message : err))
    } finally {
      setBusy(false)
    }
  }
  return <div>
    <button type="button" className="cfo-materialization-retry" disabled={busy} onClick={() => void refresh()}>
      {busy ? 'Requesting update…' : 'Update reports'}
    </button>
    {error ? <span role="alert">{error}</span> : null}
  </div>
}

// Dismissals live for the browser tab (sessionStorage), keyed by workbook and
// banner kind: a presenter can clear a banner once and navigate freely, but a
// DIFFERENT problem (say blocked → disconnected) still surfaces on its own.
const DISMISSED_BANNERS_STORAGE_KEY = 'fg.cfo.materialization-banner.dismissed'

function readDismissedBanners(): Set<string> {
  try {
    const raw = window.sessionStorage.getItem(DISMISSED_BANNERS_STORAGE_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return new Set(Array.isArray(parsed) ? parsed.filter((key): key is string => typeof key === 'string') : [])
  } catch {
    return new Set()
  }
}

function writeDismissedBanners(keys: Set<string>) {
  try {
    window.sessionStorage.setItem(DISMISSED_BANNERS_STORAGE_KEY, JSON.stringify([...keys]))
  } catch {
    // Storage blocked (private mode, embeds): the dismissal still holds for this mount.
  }
}

function DismissableBanner({ dismissKey, className, role, live, children }: {
  dismissKey: string
  className: string
  role: 'alert' | 'status'
  live?: 'polite'
  children: ReactNode
}) {
  const [dismissed, setDismissed] = useState(readDismissedBanners)
  if (dismissed.has(dismissKey)) return null
  const dismiss = () => {
    const next = new Set(dismissed).add(dismissKey)
    writeDismissedBanners(next)
    setDismissed(next)
  }
  return (
    <div className={`cfo-materialization-banner is-dismissable ${className}`.trim()} role={role} aria-live={live}>
      {children}
      <button type="button" className="cfo-materialization-dismiss" aria-label="Dismiss this notice" title="Dismiss" onClick={dismiss}>
        ×
      </button>
    </div>
  )
}

export function CfoMaterializationBanner({
  status,
  error,
  errorAttempts = 0,
  surfaceKey,
  onRetry,
  appApi,
  workbookId,
  requestedPeriod,
  bindingSetup,
}: {
  status: CfoMaterializationStatusResponse | null
  error?: string
  errorAttempts?: number
  surfaceKey?: string
  onRetry?: () => void
  appApi?: ServerApiClient
  workbookId?: string | null
  requestedPeriod?: string | null
  /** The bootstrap's setup status for an unbound workbook (the reason reports are not set up). */
  bindingSetup?: CfoBindingSetupStatus | null
}) {
  // A dead QuickBooks connection outranks every other state: the numbers on
  // screen are frozen and nothing will update until the client re-authorizes.
  const dismissPrefix = `${trimStr(workbookId) || 'workbook'}:`
  if (status?.connection?.needs_reconnect) {
    const company = status.connection.company_name?.trim() || 'this company'
    const frozenAsOf = formatFrozenAsOf(status.connection.last_sync_at)
    return (
      <DismissableBanner dismissKey={`${dismissPrefix}disconnected`} className="is-disconnected" role="alert">
        <div>
          <b>QuickBooks is disconnected for {company}.</b>
          <span>
            {frozenAsOf
              ? `Reports are frozen at the last synced data from ${frozenAsOf} and will not update`
              : 'Reports cannot update'}
            {' '}until someone with access to {company}&rsquo;s QuickBooks reconnects it.
          </span>
        </div>
        {appApi && workbookId ? (
          <CfoMaterializationReconnectButton
            appApi={appApi}
            workbookId={workbookId}
            realmId={status.connection.realm_id ?? null}
          />
        ) : null}
      </DismissableBanner>
    )
  }
  // Connectivity, NOT staleness: we could not ASK whether the numbers are current.
  // Saying "still showing the last ready version" here told clients their data was
  // behind when the only thing that failed was the status check itself.
  if (error) {
    const persistent = errorAttempts >= ERROR_PERSISTENT_AFTER
    const httpStatus = materializationHttpStatus(error)
    if (httpStatus !== null) {
      const retryable = isRetryableMaterializationError(error)
      const message = httpStatus === 404
        ? 'This workbook is unavailable or its financial setup is incomplete.'
        : httpStatus === 401
          ? 'Your session has expired. Sign in again to continue.'
          : httpStatus === 403
            ? 'Your account does not have access to these financial reports.'
            : 'FuseGrid could not complete the report request.'
      return (
        <DismissableBanner dismissKey={`${dismissPrefix}error`} className="is-error" role="status">
          <div>
            <b>We couldn’t load your financial reports.</b>
            <span>{message}{retryable ? ' We’re trying again automatically.' : ''}</span>
          </div>
          {onRetry && httpStatus !== 401 && httpStatus !== 403 ? (
            <button type="button" className="cfo-materialization-retry" onClick={onRetry}>Try now</button>
          ) : null}
        </DismissableBanner>
      )
    }
    return (
      <DismissableBanner dismissKey={`${dismissPrefix}offline`} className="is-offline" role="status">
        <div>
          <b>We can’t reach FuseGrid right now.</b>
          <span>
            {error}{' '}
            {status
              ? 'Everything below is your last completed update and has not changed.'
              : 'Everything below is the last version that finished.'}{' '}
            {persistent
              ? `Still no connection after ${errorAttempts} tries — check your internet, then reload this page.`
              : 'We’re trying again automatically.'}
          </span>
        </div>
        {onRetry ? (
          <button type="button" className="cfo-materialization-retry" onClick={onRetry}>
            Try now
          </button>
        ) : null}
      </DismissableBanner>
    )
  }
  if (!status) return null
  // The top-level refresh state covers every CFO report. A ready cash surface
  // should not claim that its own numbers are stale merely because, for
  // example, A/R is still rebuilding in the background.
  const activeSurfaceStatus = surfaceKey ? status.surfaces?.[surfaceKey]?.status : null
  const activeSurfaceReady = activeSurfaceStatus === 'ready'
  if (activeSurfaceReady) return null
  if (status.status === 'first_run' && !materializationActive(status)) {
    return <DismissableBanner dismissKey={`${dismissPrefix}first-run-idle`} className="" role="status">
      <div><b>The first report update has not completed.</b><span>Request an update to prepare these reports.</span></div>
      {appApi && workbookId ? <CfoReportRefreshButton appApi={appApi} workbookId={workbookId} requestedPeriod={requestedPeriod} onQueued={onRetry} /> : null}
    </DismissableBanner>
  }
  if (status.status === 'first_run') {
    const pct = Math.max(2, Math.min(35, Math.round(Number(status.refresh?.pct ?? 8))))
    return (
      <DismissableBanner dismissKey={`${dismissPrefix}first-run`} className="" role="status" live="polite">
        <div>
          <b>Preparing reports from current QuickBooks data.</b>
          <span>This workbook will appear as soon as the first report package is ready.</span>
        </div>
        <div className="cfo-materialization-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
          <span style={{ width: `${pct}%` }} />
        </div>
        {status.refresh?.stage_label ? <em>{status.refresh.stage_label}</em> : null}
      </DismissableBanner>
    )
  }
  if (status.status === 'refreshing') {
    const pct = Math.max(2, Math.min(99, Math.round(Number(status.refresh?.pct ?? 12))))
    return (
      <DismissableBanner dismissKey={`${dismissPrefix}refreshing`} className="" role="status" live="polite">
        <div>
          <b>Updating reports to current QuickBooks data.</b>
          <span>Showing the last ready version while this finishes.</span>
        </div>
        <div className="cfo-materialization-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
          <span style={{ width: `${pct}%` }} />
        </div>
        {status.refresh?.stage_label ? <em>{status.refresh.stage_label}</em> : null}
      </DismissableBanner>
    )
  }
  if (status.status === 'failed' || status.status === 'stale') {
    return (
      <DismissableBanner dismissKey={`${dismissPrefix}${status.status}`} className="is-failed" role="status">
        <div>
          <b>These numbers are from your last completed update.</b>
          <span>{status.status === 'failed' ? 'The most recent refresh didn’t finish.' : 'A report update is available.'}</span>
        </div>
        {appApi && workbookId ? <CfoReportRefreshButton appApi={appApi} workbookId={workbookId} requestedPeriod={requestedPeriod} onQueued={onRetry} /> : null}
      </DismissableBanner>
    )
  }
  if (status.status === 'no_binding') {
    // Binding happens automatically after a complete QuickBooks sync, and an
    // editor can set it up once accounts are synced; say why it has not
    // happened yet instead of implying the company is unconnected.
    const layoutNeedsReview = bindingSetup?.state === 'layout_needs_review'
    const needsAttention = bindingSetup?.needs_attention === true || layoutNeedsReview
    return (
      <DismissableBanner dismissKey={`${dismissPrefix}no-binding`} className={needsAttention ? 'is-blocked' : ''} role="status">
        <div>
          <b>
            {layoutNeedsReview
              ? 'The statement layout needs review before CFO reports can be set up.'
              : needsAttention ? 'CFO reports need attention before they can be set up.' : 'CFO reports are not set up yet.'}
          </b>
          <span>
            {trimStr(bindingSetup?.message)
              || humanizeStaleReasons(status.stale_reasons)
              || 'CFO reports are not set up for this workbook yet.'}
          </span>
        </div>
        {bindingSetup?.can_set_up === true && appApi && workbookId
          ? <CfoBindingSetupButton appApi={appApi} workbookId={workbookId} requestedPeriod={requestedPeriod} onSetUp={onRetry} />
          : null}
      </DismissableBanner>
    )
  }
  if (status.status === 'blocked') {
    return (
      <DismissableBanner dismissKey={`${dismissPrefix}blocked`} className="is-blocked" role="status">
        <div>
          <b>Reports need attention before they can update.</b>
          <span>
            {humanizeStaleReasons(status.stale_reasons)
              || 'A report prerequisite is missing.'}
            {' '}Your accountant can resolve this — no action needed from you.
          </span>
        </div>
      </DismissableBanner>
    )
  }
  return null
}
